/**
 * MessageItem Component
 * Individual message display with role-based styling
 */

import { Children, useState, useMemo, memo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Tooltip } from 'antd';
import { DislikeFilled, LikeFilled } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeature } from '@/hooks/useFeature';
import { FilePreviewModal, WebSources } from '@/components/common';
import { PersonalConnectorNotice } from '@modules/connector_library/components/PersonalConnectorNotice';
import { SkillBadges } from './SkillBadges';
import { TypingIndicator } from './TypingIndicator';
import { LiveStatusLine } from './LiveStatusLine';
import { FileAttachmentCard } from './FileAttachmentCard';
import { SkillFeedbackButton } from './SkillFeedbackButton';
import { MessageRating, type MessageFeedback } from './MessageRating';
import { ExportToCloudModal } from './ExportToCloudModal';
import ThinkingBanner from './ThinkingBanner';
import { AgentHandoffCard } from './AgentHandoffCard';
import { PausedAnswer } from './PausedAnswer';
import { useBrand, useBrandAssets, getAgentCoin } from '@/brand';
import { hasAgentAvatarSets } from '@/brand/agent-avatars';
import { BotAvatar } from '@/components/common/BotAvatar';
// Leaf import, not the projects barrel — the barrel pulls in the project pages
// (and the Sidebar through them), which the chat surface must not depend on.
import { PersonAvatar } from '@modules/projects/components/PersonAvatar';
import { useTheme } from '@/hooks/useTheme';
import type { TextureColor } from '@/brand/textures';
import type { Message, ThinkingStep } from '@/types/models';
import './MessageItem.css';

// Custom SVG icons matching ChatGPT style
const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ForkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="18" r="3" />
    <circle cx="6" cy="6" r="3" />
    <circle cx="18" cy="6" r="3" />
    <path d="M18 9v2c0 .6-.4 1-1 1H7c-.6 0-1-.4-1-1V9" />
    <path d="M12 12v3" />
  </svg>
);

/**
 * Branch-to-another-agent: an arrow running into an agent's head.
 *
 * An earlier version reused the fork silhouette with one node swapped, on the
 * theory that "same gesture, different outcome" should look related. In
 * practice the two icons were near-indistinguishable at 16px, which is the only
 * size that matters here. Sharing no geometry with ForkIcon is the point — the
 * arrow reads as "go to", and the head says who you're going to.
 */
const AgentForkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* → */}
    <line x1="2" y1="12" x2="10" y2="12" />
    <polyline points="7 8.5 10.5 12 7 15.5" />
    {/* the agent being handed to */}
    <rect x="13.5" y="7" width="9" height="8" rx="2.5" />
    <circle cx="16.4" cy="11" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="19.6" cy="11" r="0.9" fill="currentColor" stroke="none" />
    <line x1="18" y1="4" x2="18" y2="7" />
  </svg>
);

const ExportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

interface MessageItemProps {
  message: Message;
  onEditMessage?: (message: Message) => void;
  onBranch?: (message: Message, clickEvent?: { x: number; y: number }) => void;
  /** Open the agent chooser to hand the conversation off from this message. */
  onBranchToAgent?: (message: Message) => void;
  /** Jump to the conversation on the other side of a handoff card. */
  onHandoffTeleport?: (conversationId: string, messageId?: string) => void;
  /** Open the linked conversation beside this one instead of navigating. */
  onHandoffSplit?: (conversationId: string) => void;
  onScrollToQuote?: (quotedText: string) => void;
  /** Open an agent-generated artifact in the right-side panel (by file_id). */
  onArtifactOpen?: (fileId: string) => void;
  /** Continue this answer the output cap paused (Claude.ai's "Continue").
   *  Set by the list only when it may be continued now. */
  onContinue?: () => Promise<void>;
  isLastUserMessage?: boolean;
  isBranchPoint?: boolean;
  activeSkills?: string[];
  thinkingSteps?: ThinkingStep[];
  /** Active ChatGPT-style indicator phase (only meaningful while streaming). */
  thinkingPhase?: 'uploading' | 'opening' | 'preparing' | 'working' | 'thinking';
  /** Filename for the `reading` phase. */
  thinkingFilename?: string;
  /** Override label for the thinking indicator — live progress sentences
   *  ("Reading wiki page 3/12…", "Drafting the document…"). */
  thinkingText?: string;
  /** Epoch ms when the current status started — drives the "· Ns" elapsed
   *  counter inside LiveStatusLine (ticker is internal to that component). */
  thinkingSince?: number;
  revealThinking?: boolean;
  showSkillLabels?: boolean;
  isStreaming?: boolean;
  conversationId?: string;
  conversationTitle?: string;
  availableSkills?: Array<{ skill_id: string; skill_name: string }>;
  existingFeedback?: Array<{ skill_id: string; skill_name: string }>;
  onFeedbackChange?: (messageId: string, skills: Array<{ skill_id: string; skill_name: string }>) => void;
  existingRating?: 'like' | 'dislike' | null;
  existingRatingReasons?: string[] | null;
  existingRatingDescription?: string | null;
  onRatingChange?: (messageId: string, feedback: MessageFeedback) => void;
  botColor?: TextureColor | null;
  botName?: string;
  botVariant?: number | null;
  /** The assistant's avatar when it is neither a named agent nor the brand —
   *  the Guide on the Learn page wears its question mark, not the logo. */
  avatar?: React.ReactNode;
  /** Label user messages with who wrote them. On in shared projects only:
   *  outside one every user message is by the same person, and a name tag on
   *  each of them would be noise. */
  showSenderIdentity?: boolean;
  /** In a shared thread, the first answer that used the viewer's own account
   *  to a service: say, once, that the thread's members see it (item 65A). */
  personalSharedNotice?: boolean;
  /** When true, the fork button is rendered disabled (cap reached) with
   *  a tooltip explaining how to proceed. */
  branchDisabled?: boolean;
  /** Tooltip text shown on the disabled fork button. */
  branchDisabledReason?: string;
  /** A review of someone else's conversation (Insights → Feedback): nothing
   *  that writes or opens the owner's files — no edit, no export, attachment
   *  cards shown but not opened. Copy stays. */
  readOnly?: boolean;
  /** The feedback people left on this answer, for a review: a small marker
   *  beside the answer's actions. */
  feedbackMarker?: { likes: number; dislikes: number } | null;
  /** When given, an attachment card opens through it instead of the built-in
   *  preview (the feedback review's own, recorded route). The memo below
   *  ignores callback identity: pass a stable function. */
  onAttachmentOpen?: (attachmentId: string) => void;
}

/**
 * Some models prefix list items with a literal bullet glyph (e.g. "- • text"
 * or "• text"), which renders as a double bullet (the markdown disc + the
 * literal glyph). Normalize: drop a glyph that follows a list marker, and turn
 * standalone bullet-glyph lines into proper markdown list items.
 */
const _BULLET_GLYPHS = '•·●‣▪◦∙';
function normalizeBulletGlyphs(text: string): string {
  if (!text) return text;
  return text
    // "- • item" / "* • item"  →  "- item"  (remove redundant glyph after marker)
    .replace(new RegExp(`^(\\s*[-*+]\\s+)[${_BULLET_GLYPHS}]\\s*`, 'gm'), '$1')
    // "• item" (glyph as the only marker)  →  "- item"
    .replace(new RegExp(`^(\\s*)[${_BULLET_GLYPHS}]\\s+`, 'gm'), '$1- ');
}

/** Memoized YouTube embed — rendered outside ReactMarkdown to prevent flickering */
const YouTubeEmbed = memo(function YouTubeEmbed({ videoId, startParam }: { videoId: string; startParam: string }) {
  const { t } = useLanguage();
  return (
    <div style={{ maxWidth: '480px', margin: '16px auto' }}>
      <div style={{
        position: 'relative',
        paddingBottom: '56.25%',
        height: 0,
        overflow: 'hidden',
        borderRadius: '10px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
      }}>
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}${startParam}`}
          title={t('chat.youtubeVideo')}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '10px',
          }}
        />
      </div>
    </div>
  );
});

function MessageItemInner({ message, onEditMessage, onBranch, onBranchToAgent, onHandoffTeleport, onHandoffSplit, onScrollToQuote, onArtifactOpen, onContinue, isLastUserMessage = false, isBranchPoint = false, activeSkills = [], thinkingSteps, thinkingPhase, thinkingFilename, thinkingText, thinkingSince, revealThinking, showSkillLabels, isStreaming: isStreamingProp, conversationId, conversationTitle, availableSkills = [], existingFeedback, onFeedbackChange, existingRating, existingRatingReasons, existingRatingDescription, onRatingChange, botColor, botName, botVariant, avatar, showSenderIdentity = false, personalSharedNotice = false, branchDisabled = false, branchDisabledReason, readOnly = false, feedbackMarker = null, onAttachmentOpen }: MessageItemProps) {
  const { icon: brandIcon } = useBrandAssets();
  const { isDark } = useTheme();
  // The coloured coin is part of the textured (M²) avatar look: a brand without
  // avatar sets shows its own mark instead.
  const brand = useBrand();
  const hasTextures = hasAgentAvatarSets(brand);
  const assistantIcon = botColor && hasTextures ? getAgentCoin(botColor, isDark) : brandIcon;
  // Prefer the agent's texture whenever we have a name — the color is derived
  // from the name if not explicitly set. Only falls back to the brand logo
  // when the message isn't associated with a named agent at all.
  const hasBotTexture = !!botName;
  const { t, direction: uiDirection } = useLanguage();
  const messageRatingEnabled = useFeature('agents.message_rating');
  const skillFeedbackEnabled = useFeature('skills.activation_feedback');
  const exportCloudEnabled = useFeature('export.cloud_docs');
  const isUser = message.role === 'user';

  // User initials no longer rendered (user avatar removed for cleaner look)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  /** The branch menu is per-message, so it lives here rather than in a parent. */
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  /** Viewport coords for the portalled menu — see the render for why it can't
   *  simply be absolutely positioned inside the message. */
  const [branchMenuPos, setBranchMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  /** The agent's colour, resolved from the button's own position in the tree.
   *  Needed because the menu is portalled to <body>: custom properties inherit
   *  through the DOM, so a portalled node stops seeing the panel's
   *  `--agent-hue` and silently falls back to the global brand value — which is
   *  why the menu's frame came out brand-green regardless of the agent. */
  const [branchMenuHue, setBranchMenuHue] = useState<string>('');
  const branchBtnRef = useRef<HTMLButtonElement>(null);

  const toggleBranchMenu = () => {
    if (!branchMenuOpen && branchBtnRef.current) {
      const cs = getComputedStyle(branchBtnRef.current);
      setBranchMenuHue(
        cs.getPropertyValue('--agent-hue').trim()
        || cs.getPropertyValue('--primary').trim()
      );
      const r = branchBtnRef.current.getBoundingClientRect();
      const MENU_W = 268;
      // Keep the menu on screen near the right edge, and flip above the button
      // when there isn't room below it.
      const left = Math.min(Math.max(8, r.left), window.innerWidth - MENU_W - 8);
      const openUp = window.innerHeight - r.bottom < 190;
      setBranchMenuPos({ top: openUp ? r.top - 178 : r.bottom + 6, left });
    }
    setBranchMenuOpen(o => !o);
  };
  // Dismiss the branch menu the way every other menu in the app does.
  useEffect(() => {
    if (!branchMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      // The menu is portalled to <body>, so it is NOT inside the container —
      // testing only for the container closed it on mousedown and the item
      // unmounted before its click could fire. Both count as "inside".
      const el = e.target as Element;
      if (el?.closest?.('.branch-menu-container') || el?.closest?.('.branch-menu')) return;
      setBranchMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBranchMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [branchMenuOpen]);

  const [previewFile, setPreviewFile] = useState<{ filePath?: string; fileId?: string; fileName: string; fileType: string } | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // Merge streaming skills (activeSkills prop) with persisted skills (message.active_skills)
  const isStreaming = isStreamingProp || activeSkills.length > 0;
  const mergedSkills = useMemo(() => {
    const persisted = message.active_skills || [];
    const streaming = activeSkills || [];
    // Excel tool activations stay out of the badge row: the thinking banner's
    // sub-process lines ("Analyzing the data…") already narrate them, and a
    // pill per lookup turned a data question into a wall of chips. Real
    // skills (design, artifacts, search) keep their badges.
    return [...new Set([...persisted, ...streaming])]
      .filter((sk) => !/^excel[_ ]/i.test(String(sk)));
  }, [message.active_skills, activeSkills]);

  // Merge streaming thinking steps with persisted ones
  const allThinkingSteps = useMemo(() => {
    if (thinkingSteps && thinkingSteps.length > 0) return thinkingSteps;
    return message.thinking_steps || [];
  }, [thinkingSteps, message.thinking_steps]);

  // Check if message was stopped by user.
  //
  // Two sources, and both are needed. `[STOPPED]` is the LIVE marker the
  // panel appends to the `temp-` message the moment Stop is pressed — it
  // exists only in that in-memory transcript. `interrupted` is STORED truth,
  // flattened by `conversationService.getMessages` from the
  // `message_metadata.interrupted` that `_persist_interrupted_turn` writes.
  //
  // Reading only the literal was the bug behind "I stopped it, but when I
  // came back the answer looked complete": a reload rebuilds the transcript
  // from the database, where no `[STOPPED]` string exists, so the note
  // silently disappeared from a turn the database still called interrupted.
  // The AoA panel has always rendered its note from the stored flag; this
  // makes the regular chat agree with it, and with the database.
  const wasStopped = message.interrupted === true || message.content?.includes('[STOPPED]');
  const displayContent = wasStopped
    ? message.content?.replace('[STOPPED]', '').trim()
    : message.content;
  const hasRealContent = displayContent && displayContent !== '...' && displayContent !== '';

  // Extract YouTube URLs from content — render them as stable iframes outside ReactMarkdown
  // to prevent flickering during streaming re-renders
  const isAssistant = message.role === 'assistant';
  const { cleanContent, youtubeVideos } = useMemo(() => {
    if (!displayContent || !isAssistant) return { cleanContent: displayContent, youtubeVideos: [] as string[] };
    const videos: string[] = [];
    const seen = new Set<string>();
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})(?:[^\s)]*)/g;
    let match;
    while ((match = regex.exec(displayContent)) !== null) {
      if (!seen.has(match[1])) {
        seen.add(match[1]);
        videos.push(match[1]);
      }
    }
    // Remove YouTube URLs from markdown so ReactMarkdown doesn't render them as links
    let cleaned = videos.length > 0
      ? displayContent.replace(/\[([^\]]*)\]\((?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)[\w-]{11}[^\s)]*\)/g, '')
        .replace(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)[\w-]{11}[^\s)"]*/g, '')
      : displayContent;
    cleaned = normalizeBulletGlyphs(cleaned);
    return { cleanContent: cleaned, youtubeVideos: videos };
  }, [displayContent, isAssistant]);

  // Default export file name: conversation title + date
  const exportFileName = useMemo(() => {
    const date = new Date().toISOString().slice(0, 10);
    if (conversationTitle) return `${conversationTitle} - ${date}`;
    return date;
  }, [conversationTitle]);

  // Handle copy message (copy without the [STOPPED] marker)
  const handleCopy = () => {
    if (displayContent) {
      navigator.clipboard.writeText(displayContent);
      setCopiedMessageId(message.message_id);
      setTimeout(() => setCopiedMessageId(null), 2000);
    }
  };

  // Handle edit message
  const handleEdit = () => {
    if (onEditMessage) {
      onEditMessage(message);
    }
  };

  // Render user message with inline code properly isolated for RTL
  const renderUserMessage = (content: string) => {
    // Split by inline code patterns (backticks or code-like content)
    // Match: `code`, "quoted strings", function(), or code patterns
    const parts = content.split(/(`[^`]+`|"[^"]+"|'[^']+'|\w+\([^)]*\))/g);

    return parts.map((part, index) => {
      // Check if this part looks like code
      const isCode = /^`[^`]+`$/.test(part) || // backtick code
                     /^\w+\([^)]*\)$/.test(part); // function call like print()

      if (isCode) {
        // Remove backticks if present and wrap in LTR code element
        const codeContent = part.replace(/^`|`$/g, '');
        return <code key={index} dir="ltr" style={{ unicodeBidi: 'isolate' }}>{codeContent}</code>;
      }
      return part;
    });
  };

  // Detect if text is primarily RTL (Hebrew/Arabic)
  const isRTL = (text: string | undefined): boolean => {
    if (!text) return false;
    // Match all Hebrew and Arabic characters
    const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF]/g;
    const rtlChars = text.match(rtlRegex)?.length || 0;
    // Get text without code blocks for accurate detection
    const sampleText = text.replace(/```[\s\S]*?```/g, '').replace(/`[^`]+`/g, '').slice(0, 500);
    const totalChars = sampleText.replace(/\s/g, '').length;
    return totalChars > 0 ? rtlChars / totalChars > 0.3 : false;
  };

  // Detect RTL for individual text elements (paragraphs, list items, etc.)
  const getTextDirection = (text: string): 'rtl' | 'ltr' => {
    if (!text) return 'ltr';
    const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF]/;
    // Check first non-whitespace character direction
    const trimmed = text.trim();
    if (rtlRegex.test(trimmed.charAt(0))) return 'rtl';
    // Also check if majority is RTL
    const rtlChars = (text.match(/[\u0590-\u05FF\u0600-\u06FF]/g) || []).length;
    const latinChars = (text.match(/[a-zA-Z]/g) || []).length;
    return rtlChars > latinChars ? 'rtl' : 'ltr';
  };

  // Extract text content from React children
  const getChildText = (children: React.ReactNode): string => {
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) return children.map(getChildText).join('');
    if (children && typeof children === 'object' && 'props' in children) {
      return getChildText((children as any).props.children);
    }
    return '';
  };

  // A list's first item (its children are items and the newlines between them).
  const firstElement = (children: React.ReactNode): React.ReactNode =>
    Children.toArray(children).find((c) => typeof c === 'object') ?? null;

  const textDirection = isRTL(message.content) ? 'rtl' : 'ltr';

  // Agent-handoff markers aren't chat turns — they're the thread's record of
  // crossing into (or out of) another agent. Render the link card instead of a
  // bubble, and skip the whole action rail: copy / edit / fork / rate all mean
  // nothing for a navigation bookmark.
  // Gated on the payload alone, never on the callback: a caller that forgot to
  // wire onHandoffTeleport should get an inert card, not the raw
  // "[Agent handoff] …" system text leaking into the transcript.
  if (message.agent_handoff) {
    return (
      <div
        className="message-group handoff"
        data-message-id={message.message_id}
      >
        <AgentHandoffCard
          handoff={message.agent_handoff}
          onTeleport={(convId, msgId) => onHandoffTeleport?.(convId, msgId)}
          onOpenSplit={onHandoffSplit ? (convId) => onHandoffSplit(convId) : undefined}
        />
      </div>
    );
  }

  return (
    <div
      className={`message-group ${message.role}${isBranchPoint ? ' branch-point-message' : ''}`}
      data-message-id={message.message_id}
    >
      {/* Branch point label */}
      {isBranchPoint && (
        <div className="branch-point-label">
          <ForkIcon />
          <span>{t('chat.branchPoint')}</span>
        </div>
      )}
      {/* Avatar — assistant only (user messages have no avatar) */}
      {!isUser && (
        <div className="message-avatar">
          {avatar ?? (hasBotTexture ? (
            <BotAvatar
              name={botName!}
              size={32}
              colorOverride={botColor ?? undefined}
              variantOverride={botVariant ?? undefined}
            />
          ) : (
            <img src={assistantIcon} alt="AI" className="avatar-logo" />
          ))}
        </div>
      )}

      <div className="message-content">
        {/* Sender tag — shared projects only. Bubble colours are deliberately
            untouched; identity is carried by the tag, not by recolouring, so a
            transcript stays readable in both themes and every brand. */}
        {isUser && showSenderIdentity && (message.sender_name || message.sender_email) && (
          <div className="message-sender-tag">
            <PersonAvatar
              seed={message.sender_user_id}
              name={message.sender_name}
              email={message.sender_email}
              tenantName={message.sender_tenant_name}
              size={20}
            />
            <span
              className="message-sender-name"
              title={[message.sender_name || message.sender_email, message.sender_tenant_name]
                .filter(Boolean).join(' · ')}
            >
              {message.sender_name || message.sender_email}
            </span>
          </div>
        )}
        {isUser ? (() => {
          const content = message.content || '';
          // Detect reply pattern: [reply: "quoted text"]\nmessage
          const replyMatch = content.match(/^\[reply: "(.+?)"\]\n([\s\S]*)$/);
          const quoted = replyMatch?.[1];
          const userText = replyMatch ? replyMatch[2] : content;
          const bubble = (
            <div
              className={`message-text ${textDirection === 'rtl' ? 'rtl-text' : ''}`}
              dir={textDirection}
            >
              {renderUserMessage(userText)}
            </div>
          );
          // Attachments on a user message: render ABOVE the bubble in a
          // tight stack so the files visually belong to this message
          // rather than floating loose underneath. Mirrors Anthropic
          // and ChatGPT: attached files sit at the head of the message
          // they were sent with.
          const hasAttachments = (message.attachments?.length ?? 0) > 0;
          const attachments = hasAttachments && (
            <div className={`attachment-card-row attachment-card-row--user${(message.attachments!.length > 1) ? ' multi' : ''}`}>
              {message.attachments!.map((attachment) => (
                <FileAttachmentCard
                  key={attachment.attachment_id}
                  mode="attached"
                  fileId={attachment.file_id}
                  fileName={attachment.file_name}
                  fileSize={attachment.file_size}
                  fileType={attachment.file_type || ''}
                  source={attachment.source}
                  onClick={() => {
                    if (onAttachmentOpen) {
                      onAttachmentOpen(attachment.attachment_id);
                      return;
                    }
                    if (attachment.file_id || attachment.file_path || attachment.file_url) {
                      setPreviewFile({
                        fileId: attachment.file_id,
                        filePath: attachment.file_path || attachment.file_url,
                        fileName: attachment.file_name,
                        fileType: attachment.file_type || '',
                      });
                    }
                  }}
                />
              ))}
            </div>
          );
          if (!quoted && !hasAttachments) return bubble;
          // Stack: optional reply card, optional attachment cards, bubble —
          // all sharing the same width and visually attached as one unit.
          return (
            <div className="message-stack message-stack--user">
              {quoted && (
                <div
                  className="reply-quote-block"
                  onClick={() => onScrollToQuote?.(quoted)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="reply-quote-content">
                    <span className="reply-quote-label">{t('chat.quoteReply')}</span>
                    <span className="reply-quote-text bidi-clamp" dir="auto">{quoted}</span>
                  </div>
                </div>
              )}
              {attachments}
              {/* A files-only message has no text — don't render an empty bubble. */}
              {userText.trim() ? bubble : null}
            </div>
          );
        })() : (
          // Assistant message - markdown rendered or typing indicator
          <div
            className={`message-text ${textDirection === 'rtl' ? 'rtl-text' : ''} ${isStreaming ? 'streaming' : ''}`}
            dir={textDirection}
          >
            {/* Thinking banner — above everything when enabled */}
            {revealThinking && allThinkingSteps.length > 0 && (
              <ThinkingBanner steps={allThinkingSteps} streaming={isStreaming} />
            )}
            {/* Show skill badges above content — controlled by showSkillLabels (default: true) */}
            {(showSkillLabels !== false) && mergedSkills.length > 0 && (
              <SkillBadges skills={mergedSkills} animated={isStreaming} />
            )}
            {hasRealContent ? (
              <>
                <ReactMarkdown
                  // singleDollarTextMath: false — `$` is currency here far more
                  // often than it is math. With single-dollar math ON, an
                  // ordinary cost answer ("$240.36 total … = $239.00") has
                  // everything BETWEEN the two amounts swallowed and rendered
                  // as garbled KaTeX. Block math ($$…$$) still works.
                  remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
                  rehypePlugins={[rehypeKatex]}
                  components={{
                    // Per-paragraph direction detection
                    p({ children, ...props }: any) {
                      const text = getChildText(children);
                      const dir = getTextDirection(text);
                      return <p dir={dir} style={{ textAlign: dir === 'rtl' ? 'right' : 'left' }} {...props}>{children}</p>;
                    },
                    // A list takes the direction of its first item, so its
                    // markers hang on the side its items start from (see
                    // MessageItem.css: markers stay outside).
                    ul({ children, node, ...props }: any) {
                      return <ul dir={getTextDirection(getChildText(firstElement(children)))} {...props}>{children}</ul>;
                    },
                    ol({ children, node, ...props }: any) {
                      return <ol dir={getTextDirection(getChildText(firstElement(children)))} {...props}>{children}</ol>;
                    },
                    // Per-list-item direction detection
                    li({ children, ...props }: any) {
                      const text = getChildText(children);
                      const dir = getTextDirection(text);
                      return <li dir={dir} style={{ textAlign: dir === 'rtl' ? 'right' : 'left' }} {...props}>{children}</li>;
                    },
                    // Headings with direction
                    h1({ children, ...props }: any) {
                      const text = getChildText(children);
                      const dir = getTextDirection(text);
                      return <h1 dir={dir} style={{ textAlign: dir === 'rtl' ? 'right' : 'left' }} {...props}>{children}</h1>;
                    },
                    h2({ children, ...props }: any) {
                      const text = getChildText(children);
                      const dir = getTextDirection(text);
                      return <h2 dir={dir} style={{ textAlign: dir === 'rtl' ? 'right' : 'left' }} {...props}>{children}</h2>;
                    },
                    h3({ children, ...props }: any) {
                      const text = getChildText(children);
                      const dir = getTextDirection(text);
                      return <h3 dir={dir} style={{ textAlign: dir === 'rtl' ? 'right' : 'left' }} {...props}>{children}</h3>;
                    },
                    // Blockquotes with direction
                    blockquote({ children, ...props }: any) {
                      const text = getChildText(children);
                      const dir = getTextDirection(text);
                      return <blockquote dir={dir} {...props}>{children}</blockquote>;
                    },
                    // Links open in new tab (YouTube URLs are handled outside ReactMarkdown)
                    a({ href, children, ...props }: any) {
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    },
                    // Code blocks always LTR
                    code({ className, children, ...props }: any) {
                      const match = /language-(\w+)/.exec(className || '');
                      const codeString = String(children).replace(/\n$/, '');
                      const isInline = !className;

                      return !isInline && match ? (
                        <div className="code-block-wrapper" dir="ltr">
                          <div className="code-block-header">
                            <span className="code-language">{match[1]}</span>
                            <button
                              className="code-copy-btn"
                              onClick={(e) => {
                                navigator.clipboard.writeText(codeString);
                                // Show copied feedback
                                const btn = e.currentTarget;
                                const originalText = btn.textContent;
                                btn.textContent = t('chat.code.copied');
                                setTimeout(() => {
                                  btn.textContent = originalText;
                                }, 2000);
                              }}
                            >
                              {t('chat.code.copy')}
                            </button>
                          </div>
                          <SyntaxHighlighter
                            style={vscDarkPlus as any}
                            language={match[1]}
                            PreTag="div"
                          >
                            {codeString}
                          </SyntaxHighlighter>
                        </div>
                      ) : (
                        <code className={className} dir="ltr" {...props}>
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {cleanContent}
                </ReactMarkdown>
                {wasStopped && (
                  <div className="message-stopped">
                    {t('chat.responseStopped')}
                  </div>
                )}
                {!wasStopped && message.answer_limit_reached && (
                  <div className="message-stopped message-ui-note" dir={uiDirection}
                       data-testid="message-answer-limit-reached">
                    {t('chat.answerLimitReached')}
                  </div>
                )}
                {/* The pages the answer's web searches used, whichever model searched. */}
                {!isStreaming && <WebSources sources={message.web_sources} />}
                {/* A shared thread's first answer that used the viewer's own account says the thread sees it. */}
                {!isStreaming && personalSharedNotice && (
                  <PersonalConnectorNotice used={message.personal_connectors_used} />
                )}
                {/* YouTube videos — stable elements after text, outside ReactMarkdown */}
                {youtubeVideos.length > 0 && youtubeVideos.map((videoId) => (
                  <YouTubeEmbed key={videoId} videoId={videoId} startParam="" />
                ))}
                {!wasStopped && message.answer_cut_off && (
                  // Paused at the length cap, as Claude.ai does: the user
                  // decides whether it goes on. Continue grows THIS message;
                  // it is absent where it may not (see MessageList). Last in
                  // the answer, after its sources and videos, so it never moves.
                  <PausedAnswer onContinue={onContinue} dir={uiDirection}
                                interrupted={message.answer_cut_off_reason === 'provider_error'}
                                testId="message-answer-paused" buttonTestId="message-continue" />
                )}
              </>
            ) : wasStopped ? (
              // Stopped before any content - just show the stopped message
              <div className="message-stopped">
                {t('chat.responseStopped')}
              </div>
            ) : message.answer_cut_off ? (
              // The output cap ran out before a word of the answer: the note
              // IS the answer (never a spinner that never stops). Not "ask to
              // continue" — that would run into the same limit.
              <div className="message-stopped message-ui-note" dir={uiDirection}
                   data-testid="message-answer-cut-off">
                {t(message.answer_cut_off_reason === 'provider_error'
                  ? 'chat.answerInterrupted' : 'chat.answerCutOffEmpty')}
              </div>
            ) : (
              // Empty content during streaming — render the ChatGPT-style
              // thinking indicator. Phase + filename come from the parent
              // (driven by `type: 'thinking'` SSE events); `text` overrides
              // with live progress sentences; `since` drives the elapsed
              // counter on long steps.
              <TypingIndicator phase={thinkingPhase ?? 'thinking'} filename={thinkingFilename} text={thinkingText} since={thinkingSince} />
            )}
            {/* Persistent live status — shown even when the agent has already
                streamed text (the indicator above only renders for empty
                content), so later tool rounds ("Reading wiki page 3/12…",
                "Rendering the final document…") stay visible BELOW the text
                and are never erased by streaming tokens. */}
            {isStreaming && thinkingText && hasRealContent && (
              <LiveStatusLine text={thinkingText} since={thinkingSince} className="live-status-line--stacked" />
            )}
          </div>
        )}

        {/* Message Actions */}
        <div className={`message-actions ${isUser ? 'user-actions' : 'assistant-actions'}`}>
          {/* Copy button - for both user and assistant messages */}
          <Tooltip
            title={copiedMessageId === message.message_id ? t('chat.actions.copied') : t('chat.actions.copy')}
            overlayClassName="message-action-tooltip"
          >
            <button
              className={`message-action-btn ${copiedMessageId === message.message_id ? 'copied' : ''}`}
              onClick={handleCopy}
              aria-label={t('chat.actions.copy')}
            >
              <CopyIcon />
            </button>
          </Tooltip>

          {/* Edit button - only for the last user message */}
          {isUser && isLastUserMessage && !readOnly && (
            <Tooltip title={t('chat.actions.edit')} overlayClassName="message-action-tooltip">
              <button
                className="message-action-btn"
                onClick={handleEdit}
                aria-label={t('chat.actions.edit')}
              >
                <EditIcon />
              </button>
            </Tooltip>
          )}

          {/* ONE branch control.
              Two adjacent fork icons made the user pick between two glyphs
              before knowing what either did. Branching is a single intent —
              "continue from here" — so it's one button, and WHO continues is
              the choice inside it. */}
          {!isUser && hasRealContent && (onBranch || onBranchToAgent) && (
            <div className="branch-menu-container">
              <Tooltip
                title={t('chat.branchFrom') || 'Branch from here'}
                overlayClassName="message-action-tooltip"
              >
                <button
                  ref={branchBtnRef}
                  className={`message-action-btn fork-btn ${branchMenuOpen ? 'is-open' : ''}`}
                  onClick={toggleBranchMenu}
                  aria-label={t('chat.branchFrom') || 'Branch from here'}
                  aria-haspopup="menu"
                  aria-expanded={branchMenuOpen}
                >
                  <ForkIcon />
                </button>
              </Tooltip>
              {/* Portalled to <body> and positioned in VIEWPORT coordinates.
                  Absolutely positioning it inside the message doesn't work:
                  each message row establishes its own stacking context, so a
                  z-index here can't lift the menu above a LATER row — a handoff
                  card rendered below painted straight over it and swallowed the
                  clicks. Same approach the sidebar menus already use. */}
              {branchMenuOpen && createPortal(
                <div
                  className="branch-menu"
                  role="menu"
                  style={{
                    top: branchMenuPos.top,
                    left: branchMenuPos.left,
                    // Re-seed the inherited chain the portal broke.
                    ...(branchMenuHue ? { ['--agent-hue' as any]: branchMenuHue } : {}),
                  }}
                >
                  {onBranch && (
                    <button
                      type="button"
                      role="menuitem"
                      className="branch-menu-item"
                      disabled={branchDisabled}
                      title={branchDisabled ? (branchDisabledReason || '') : undefined}
                      onClick={(e) => {
                        setBranchMenuOpen(false);
                        if (!branchDisabled) onBranch(message, { x: e.clientX, y: e.clientY });
                      }}
                    >
                      <ForkIcon />
                      <span className="branch-menu-item-text">
                        <span className="branch-menu-item-title">
                          {t('chat.branchMenu.sameAgent') || 'Branch with this agent'}
                        </span>
                        <span className="branch-menu-item-sub">
                          {t('chat.branchMenu.sameAgentSub') || 'Explore a different direction, side by side'}
                        </span>
                      </span>
                    </button>
                  )}
                  {onBranchToAgent && (
                    <button
                      type="button"
                      role="menuitem"
                      className="branch-menu-item"
                      onClick={() => {
                        setBranchMenuOpen(false);
                        onBranchToAgent(message);
                      }}
                    >
                      <AgentForkIcon />
                      <span className="branch-menu-item-text">
                        <span className="branch-menu-item-title">
                          {t('chat.branchMenu.otherAgent') || 'Branch to another agent'}
                        </span>
                        <span className="branch-menu-item-sub">
                          {t('chat.branchMenu.otherAgentSub') || 'Hand the conversation over, with its context'}
                        </span>
                      </span>
                    </button>
                  )}
                </div>,
                document.body
              )}
            </div>
          )}

          {/* Export to Cloud button - only for assistant messages with real content */}
          {exportCloudEnabled && !isUser && hasRealContent && !readOnly && (
            <Tooltip title={t('export.actionTooltip')} overlayClassName="message-action-tooltip">
              <button
                className="message-action-btn export-btn"
                onClick={() => setShowExportModal(true)}
                aria-label={t('export.actionTooltip')}
              >
                <ExportIcon />
              </button>
            </Tooltip>
          )}

          {/* Like/Dislike rating - only for assistant messages with content */}
          {messageRatingEnabled && !isUser && hasRealContent && conversationId && !readOnly && (
            <MessageRating
              messageId={message.message_id}
              conversationId={conversationId}
              initialRating={existingRating}
              initialReasons={existingRatingReasons}
              initialDescription={existingRatingDescription}
              agentName={botName}
              onFeedbackChange={onRatingChange}
            />
          )}

          {/* Skill Feedback button - only for assistant messages */}
          {skillFeedbackEnabled && !isUser && hasRealContent && conversationId && availableSkills.length > 0 && !readOnly && (
            <SkillFeedbackButton
              messageId={message.message_id}
              conversationId={conversationId}
              availableSkills={availableSkills}
              actualSkills={message.active_skills}
              existingFeedback={existingFeedback}
              onFeedbackChange={onFeedbackChange}
            />
          )}

          {/* What people said about this answer (a review only). */}
          {!isUser && feedbackMarker && (feedbackMarker.likes > 0 || feedbackMarker.dislikes > 0) && (
            <span className="message-feedback-marker" data-testid="message-feedback-marker">
              {feedbackMarker.likes > 0 && (
                <span
                  className="message-feedback-marker__item message-feedback-marker__item--like"
                  aria-label={t('insights.feedback.marker.likes', { count: feedbackMarker.likes })}
                  title={t('insights.feedback.marker.likes', { count: feedbackMarker.likes })}
                >
                  <LikeFilled aria-hidden />
                  {feedbackMarker.likes > 1 && <span>{feedbackMarker.likes}</span>}
                </span>
              )}
              {feedbackMarker.dislikes > 0 && (
                <span
                  className="message-feedback-marker__item message-feedback-marker__item--dislike"
                  aria-label={t('insights.feedback.marker.dislikes', { count: feedbackMarker.dislikes })}
                  title={t('insights.feedback.marker.dislikes', { count: feedbackMarker.dislikes })}
                >
                  <DislikeFilled aria-hidden />
                  {feedbackMarker.dislikes > 1 && <span>{feedbackMarker.dislikes}</span>}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Attachments for ASSISTANT messages only — rendered below the
            bubble (rare case). User-message attachments live INSIDE the
            user-message stack above so the file visually belongs to the
            message it was sent with. */}
        {!isUser && message.attachments && message.attachments.length > 0 && (
          <div className="attachment-card-row">
            {message.attachments.map((attachment) => {
              // Agent-generated artifacts open in the right-side panel; regular
              // assistant attachments use the file preview modal.
              const artifactId = (attachment as any).artifact_id
                || ((attachment as any).is_agent_generated ? attachment.file_id : null);
              return (
                <FileAttachmentCard
                  key={attachment.attachment_id}
                  mode="attached"
                  fileId={attachment.file_id}
                  fileName={attachment.file_name}
                  fileSize={attachment.file_size}
                  fileType={attachment.file_type || ''}
                  source={attachment.source}
                  onClick={() => {
                    if (onAttachmentOpen) {
                      onAttachmentOpen(attachment.attachment_id);
                      return;
                    }
                    if (artifactId && onArtifactOpen) {
                      onArtifactOpen(artifactId);
                      return;
                    }
                    if (attachment.file_id || attachment.file_path || attachment.file_url) {
                      setPreviewFile({
                        fileId: attachment.file_id,
                        filePath: attachment.file_path || attachment.file_url,
                        fileName: attachment.file_name,
                        fileType: attachment.file_type || '',
                      });
                    }
                  }}
                />
              );
            })}
          </div>
        )}

        <FilePreviewModal
          visible={!!previewFile}
          filePath={previewFile?.filePath}
          fileId={previewFile?.fileId}
          fileName={previewFile?.fileName || ''}
          fileType={previewFile?.fileType}
          onClose={() => setPreviewFile(null)}
        />

        {exportCloudEnabled && (
          <ExportToCloudModal
            visible={showExportModal}
            content={displayContent || ''}
            defaultFileName={exportFileName}
            onClose={() => setShowExportModal(false)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * MessageItem is wrapped in React.memo with a custom comparator so its
 * expensive ReactMarkdown + SyntaxHighlighter + KaTeX tree only re-renders
 * when something visually meaningful changes.
 *
 * Why a custom comparator (not the default shallow one): the parent
 * MessageList forwards callback props (onEditMessage, onBranch, etc.)
 * straight from ConversationPanel without useCallback wrappers, so the
 * function identities change on every parent render. Shallow memo would
 * never short-circuit. The comparator below skips callback identity
 * checks and focuses on the props that actually drive what's rendered:
 * message content/role/metadata, streaming/highlight flags, and the
 * per-message feedback state.
 *
 * Without this, every animation frame during streaming re-parsed markdown
 * for EVERY message in the list, causing visible jank on medium-length
 * conversations.
 */
export const MessageItem = memo(MessageItemInner, (prev, next) => {
  // Different message id → must re-render.
  if (prev.message.message_id !== next.message.message_id) return false;
  // Content changes drive the streaming reveal — always re-render on this.
  if (prev.message.content !== next.message.content) return false;
  // Metadata changes (model, mode, attachments, finish reason) — re-render.
  if (prev.message.created_at !== next.message.created_at) return false;
  if (prev.message.model_name !== next.message.model_name) return false;
  if (prev.message.mode_label !== next.message.mode_label) return false;
  if (prev.message.web_sources !== next.message.web_sources) return false;
  if (prev.message.personal_connectors_used !== next.message.personal_connectors_used) return false;
  if (prev.personalSharedNotice !== next.personalSharedNotice) return false;
  // Streaming-related flags that affect the cursor/typing indicator.
  if (prev.isStreaming !== next.isStreaming) return false;
  // Thinking-indicator label changes (phase + live progress sentences).
  if (prev.thinkingPhase !== next.thinkingPhase) return false;
  if (prev.thinkingFilename !== next.thinkingFilename) return false;
  if (prev.thinkingText !== next.thinkingText) return false;
  // Status start-time drives the elapsed counter (ticker itself is internal
  // to LiveStatusLine — this only changes when the status changes).
  if (prev.thinkingSince !== next.thinkingSince) return false;
  if (prev.isLastUserMessage !== next.isLastUserMessage) return false;
  if (prev.isBranchPoint !== next.isBranchPoint) return false;
  // Handoff payload decides bubble-vs-card — a change here swaps the entire
  // rendered tree, so it can't be lumped in with the content check above.
  if (prev.message.agent_handoff !== next.message.agent_handoff) return false;
  // Active-skill chips render inside the message.
  if ((prev.activeSkills?.length || 0) !== (next.activeSkills?.length || 0)) return false;
  // Per-message feedback / rating affect the footer controls.
  if (prev.existingRating !== next.existingRating) return false;
  if (prev.existingRatingDescription !== next.existingRatingDescription) return false;
  if (prev.existingRatingReasons !== next.existingRatingReasons) return false;
  // Branch button availability.
  if (prev.branchDisabled !== next.branchDisabled) return false;
  // Bot identity (color/name) influences the avatar; rarely changes mid-stream.
  if (prev.botName !== next.botName) return false;
  if (prev.botColor !== next.botColor) return false;
  if (prev.avatar !== next.avatar) return false;
  // A review's read-only mode and its feedback marker.
  if (prev.readOnly !== next.readOnly) return false;
  if (prev.feedbackMarker?.likes !== next.feedbackMarker?.likes) return false;
  if (prev.feedbackMarker?.dislikes !== next.feedbackMarker?.dislikes) return false;
  // Everything else (callback identities, theme tokens, etc.) is irrelevant
  // for re-render purposes — return true to skip.
  return true;
});
